import { X, ExternalLink } from 'lucide-react';
import { ProductListing } from '../types';

interface ComparisonViewProps {
  products: ProductListing[];
  onRemoveProduct: (productId: string) => void;
  onClose: () => void;
}

export function ComparisonView({ products, onRemoveProduct, onClose }: ComparisonViewProps) {
  if (products.length === 0) {
    return null;
  }

  const prices = products.map((p) => p.price || 0).filter((p) => p > 0);
  const priceDifference = prices.length > 1 ? Math.max(...prices) - Math.min(...prices) : 0;
  const lowestPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const highestPrice = prices.length > 0 ? Math.max(...prices) : 0;

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 animate-fade-in">
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-200 dark:border-neutral-700 bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-800">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Compare Products</h2>
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1">{products.length} product{products.length !== 1 ? 's' : ''} selected</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors duration-200 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            aria-label="Close comparison"
          >
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Comparison Grid */}
        <div className="overflow-x-auto flex-1 p-3 sm:p-4 md:p-6">
          <div className="flex gap-3 sm:gap-4 min-w-min">
            {products.map((product) => (
              <div
                key={product.id}
                className="flex-shrink-0 w-72 sm:w-80 card card-hover overflow-hidden"
              >
                {/* Image */}
                <div className="relative overflow-hidden bg-neutral-100 dark:bg-neutral-800 aspect-square">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300 dark:text-neutral-600">
                      <ExternalLink size={40} className="sm:w-12 sm:h-12" />
                    </div>
                  )}
                  <button
                    onClick={() => onRemoveProduct(product.id)}
                    className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 sm:p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-colors duration-200"
                    title="Remove from comparison"
                    aria-label="Remove from comparison"
                  >
                    <X size={14} className="sm:w-[18px] sm:h-[18px]" />
                  </button>
                  {product.price && product.price === lowestPrice && prices.length > 1 && (
                    <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 bg-green-500 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-bold shadow-lg">
                      Best Price
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-2 leading-snug hover:text-accent-600 dark:hover:text-accent-400 transition-colors text-sm sm:text-base">
                    <a href={product.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {product.title}
                    </a>
                  </h3>

                  {/* Price */}
                  <div className="bg-neutral-50 dark:bg-neutral-700 rounded-lg p-3 sm:p-4 border border-neutral-200 dark:border-neutral-600">
                    <div className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                      {product.price ? `$${product.price.toLocaleString()}` : 'N/A'}
                    </div>
                    {product.price && prices.length > 1 && (
                      <div className="text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        {product.price === lowestPrice
                          ? 'Lowest price'
                          : product.price === highestPrice
                          ? `$${priceDifference.toLocaleString()} more than lowest`
                          : `$${(product.price - lowestPrice).toLocaleString()} more than lowest`}
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                    {product.condition && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Condition:</span>
                        <span className="font-medium text-neutral-900 dark:text-neutral-100">{product.condition}</span>
                      </div>
                    )}

                    {product.location && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Location:</span>
                        <span className="font-medium text-neutral-900 dark:text-neutral-100">{product.location}</span>
                      </div>
                    )}

                    {product.seller_info?.rating && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Rating:</span>
                        <span className="font-medium text-neutral-900 dark:text-neutral-100">
                          ⭐ {product.seller_info.rating}
                          {product.seller_info.review_count && ` (${product.seller_info.review_count})`}
                        </span>
                      </div>
                    )}

                    {product.marketplace && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Source:</span>
                        <span className="font-medium text-accent-600 dark:text-accent-400">
                          {product.marketplace === 'FBM'
                            ? 'Facebook Marketplace'
                            : product.marketplace === 'OfferUp'
                            ? 'OfferUp'
                            : product.marketplace === 'Craigslist'
                            ? 'Craigslist'
                            : product.marketplace === 'eBay'
                            ? 'eBay'
                            : 'Amazon'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* View Button */}
                  <a
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full btn-primary py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg flex items-center justify-center gap-1.5 sm:gap-2"
                  >
                    <ExternalLink size={14} className="sm:w-4 sm:h-4" />
                    View Full Listing
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 p-4 sm:p-6 bg-neutral-50 dark:bg-neutral-800/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          {products.length > 1 && prices.length > 1 && (
            <div className="text-xs sm:text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">Price difference: </span>
              <span className="font-bold text-neutral-900 dark:text-neutral-100">${priceDifference.toLocaleString()}</span>
              <span className="text-neutral-500 dark:text-neutral-400 ml-2">
                ({lowestPrice.toLocaleString()} - {highestPrice.toLocaleString()})
              </span>
            </div>
          )}
          <button 
            onClick={onClose} 
            className="btn-secondary px-5 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm w-full sm:w-auto"
            aria-label="Close comparison view"
          >
            Close Comparison
          </button>
        </div>
      </div>
    </div>
  );
}
